import React, { useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Award, Download, Share2, Copy, Loader2, ExternalLink, Linkedin } from "lucide-react";

export const certImageUrl = (id) => `${API}/cert/${id}.png`;
export const certPageUrl = (id) => `${window.location.origin}/cert/${id}`;
export const certOgUrl = (id) => `${API}/cert/${id}/share`;

export const linkedInCaption = (cert) =>
  `Just completed "${cert.course_title}" (Level ${cert.level}) on Pitch Craft — the AI sales coach. ${cert.lesson_count} lessons, real roleplays against AI customers, and a scorecard after every conversation. On to the next level. #sales #salestraining #PitchCraft`;

export function postToLinkedIn(cert) {
  const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certOgUrl(cert.id))}`;
  window.open(url, "_blank", "noopener,noreferrer,width=720,height=640");
  navigator.clipboard?.writeText(linkedInCaption(cert)).then(
    () => toast.success("Caption copied — paste it into your LinkedIn post. The certificate image attaches automatically."),
    () => toast("LinkedIn opened — the certificate image attaches automatically.")
  );
}

export function useCertificateShare(cert) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(certImageUrl(cert.id));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pitchcraft-certificate-${cert.course_id}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download the certificate."); } finally { setBusy(false); }
  };
  const share = async () => {
    const text = `I've just completed "${cert.course_title}" on Pitch Craft — the AI sales coach.`;
    setBusy(true);
    try {
      if (navigator.canShare) {
        const res = await fetch(certImageUrl(cert.id));
        const file = new File([await res.blob()], `pitchcraft-certificate-${cert.course_id}.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) { await navigator.share({ title: "My Pitch Craft certificate", text, files: [file], url: certPageUrl(cert.id) }); return; }
      }
      if (navigator.share) { await navigator.share({ title: "My Pitch Craft certificate", text, url: certPageUrl(cert.id) }); return; }
      await navigator.clipboard.writeText(certPageUrl(cert.id));
      toast.success("Certificate link copied — paste it on LinkedIn or WhatsApp.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't share right now."); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(certPageUrl(cert.id)); toast.success("Link copied"); } catch { toast.error("Couldn't copy"); }
  };
  return { busy, download, share, copy };
}

export function CertificateActions({ cert, compact = false }) {
  const { busy, download, share, copy } = useCertificateShare(cert);
  const size = compact ? "sm" : "default";
  return (
    <div className="flex flex-wrap gap-2" data-testid="certificate-actions">
      <Button onClick={() => postToLinkedIn(cert)} size={size} className="bg-[#0A66C2] hover:bg-[#004182] text-white font-semibold rounded-full" data-testid="certificate-linkedin-btn">
        <Linkedin className="w-4 h-4 mr-1.5" /> Post to LinkedIn
      </Button>
      <Button onClick={share} disabled={busy} size={size} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-full" data-testid="certificate-share-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Share2 className="w-4 h-4 mr-1.5" />} Share
      </Button>
      <Button onClick={download} disabled={busy} size={size} variant="outline" className="rounded-full" data-testid="certificate-download-btn"><Download className="w-4 h-4 mr-1.5" /> Download PNG</Button>
      <Button onClick={copy} size={size} variant="outline" className="rounded-full" data-testid="certificate-copy-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy link</Button>
      <a href={certPageUrl(cert.id)} target="_blank" rel="noreferrer" className="inline-flex items-center text-sm text-slate-500 hover:text-emerald-700 px-2" data-testid="certificate-open-link"><ExternalLink className="w-4 h-4 mr-1" /> Open</a>
    </div>
  );
}

export function CertificateDialog({ cert, open, onClose }) {
  if (!cert) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-3xl" data-testid="certificate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Certificate of Completion</DialogTitle>
          <DialogDescription>{cert.course_title} · Level {cert.level} · issued {new Date(cert.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</DialogDescription>
        </DialogHeader>
        <img src={certImageUrl(cert.id)} alt={`Certificate for ${cert.course_title}`} className="w-full rounded-xl border border-slate-200 shadow-md" data-testid="certificate-image" />
        <CertificateActions cert={cert} />
      </DialogContent>
    </Dialog>
  );
}
